import { ItemId, TreeData, TreeDestinationPosition, TreeItem } from 'tree-dnd'

import { TreeDataItem } from './types'
import { PinnedAlmanacFile } from 'types'
import { isUndefined } from 'lodash'

export function buildItem(
  id: string | number,
  data: TreeDataItem,
  children: string[] = [],
  expandedItemIds?: (string | number)[],
): TreeItem {
  return {
    id: id.toString(),
    children: children.map((i) => i.toString()),
    hasChildren: children.length > 0,
    isExpanded: expandedItemIds?.includes(id),
    data: { ...data, id: data.file_id || data.id },
  }
}

/*
 * Calculates the absolute position of the dropped item based on the position of any of the consequent nodes.
 * If there's a previous node, we do previousNode.position + 1, if there's no previous node
 * we use the nextNode as in nextNode.position - 1
 */
export function calculateAbsolutePosition(
  newTree: TreeData,
  destination: TreeDestinationPosition,
  movingUpwards: boolean,
) {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const positionAttempted = destination.index!

  const parentId = destination.parentId === '0' ? newTree.rootId : destination.parentId
  const allKeys = newTree.items[parentId].children
  const allPositions = allKeys.map((key: ItemId) => (newTree.items[key].data as PinnedAlmanacFile).position)
  const prevNodePosition = allPositions[positionAttempted - 1]
  const nextNodePosition = allPositions[positionAttempted + 1]

  return isUndefined(prevNodePosition) || (movingUpwards && !isUndefined(nextNodePosition))
    ? nextNodePosition - 1
    : prevNodePosition + 1
}

/*
 * Reshuffles nodes from a parent node after a drag and drop happened. It also calculates the absolute position
 * of the dragged node based of off the direct neighbor siblings.
 */
export function reshuffle(
  newTree: TreeData,
  source: TreeDestinationPosition,
  destination: TreeDestinationPosition,
  draggingId: ItemId,
) {
  if (isUndefined(destination.index)) return

  // Reshuffle siblings first, reassigning positions
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const movingUpwards = source.index! < destination.index!
  const parentId = destination.parentId === '0' ? newTree.rootId : destination.parentId
  const parent = newTree.items[parentId]

  if (movingUpwards) {
    parent?.children.slice(0, destination.index).forEach((childKey) => {
      const siblingData = newTree.items[childKey].data
      siblingData.position = Math.max(siblingData.position - 1, 0)
    })
  } else {
    parent?.children.slice(destination.index + 1).forEach((childKey) => {
      const siblingData = newTree.items[childKey].data
      siblingData.position = siblingData.position + 1
    })
  }

  const absolutePosition = calculateAbsolutePosition(newTree, destination, movingUpwards)

  // Set the new position for the item
  const draggedItem = newTree.items[draggingId]
  const fileItem = draggedItem.data
  fileItem.parent_id = destination.parentId === '0' ? null : parseInt(destination.parentId as string)
  fileItem.position = destination.index !== undefined ? absolutePosition : null

  return absolutePosition
}

/**
 * Parse File items into TreeData format required by tree-dnd library
 * @param items File[]
 */
export function toTree(items: TreeDataItem[], expandedItemIds?: (string | number)[]): TreeData {
  const rootId = 0
  const node2item = (item: TreeDataItem) => buildItem(item.id, item, [], expandedItemIds)

  const filteredItems = items.filter((item) => item)
  const children = filteredItems.map((item) => item.id)
  const root = buildItem(rootId, {} as TreeDataItem, children)

  return {
    rootId: rootId,
    items: {
      [root.id]: root,
      ...filteredItems.reduce((acc, i) => Object.assign(acc, { [i.id]: node2item(i) }), {}),
    },
  }
}
